const TPS_RATE = 0.05;
const TVQ_RATE = 0.09975;
const STRIPE_PERCENT = 0.029;
const STRIPE_FIXED = 0.3;
const TAX_MULTIPLIER = 1 + TPS_RATE + TVQ_RATE;

const round2 = (n) => Math.round(n * 100) / 100;

const emptyBreakdown = () => ({
  ticketPrice: 0,
  serviceFee: 0,
  stripeFee: 0,
  subtotal: 0,
  tps: 0,
  tvq: 0,
  taxes: 0,
  totalPrice: 0,
  displayPrice: 0,
});

/** Calcule la décomposition à partir du prix billet envoyé à Stripe. */
export const calculateFromTicketPrice = (ticketPrice) => {
  if (!ticketPrice || ticketPrice <= 0) {
    return emptyBreakdown();
  }

  const roundedTicket = round2(ticketPrice);
  const serviceFee = round2(roundedTicket * STRIPE_PERCENT + STRIPE_FIXED);
  const subtotal = round2(roundedTicket + serviceFee);
  const tps = round2(subtotal * TPS_RATE);
  const tvq = round2(subtotal * TVQ_RATE);
  const totalPrice = round2(subtotal + tps + tvq);

  return {
    ticketPrice: roundedTicket,
    serviceFee,
    stripeFee: serviceFee,
    subtotal,
    tps,
    tvq,
    taxes: round2(tps + tvq),
    totalPrice,
    displayPrice: totalPrice,
  };
};

/** Calcule le prix billet à partir du prix affiché final souhaité. */
export const calculateFromDisplayPrice = (displayPrice) => {
  if (!displayPrice || displayPrice <= 0) {
    return emptyBreakdown();
  }

  const target = round2(displayPrice);
  const subtotalApprox = target / TAX_MULTIPLIER;
  const ticketApprox = (subtotalApprox - STRIPE_FIXED) / (1 + STRIPE_PERCENT);

  let best = calculateFromTicketPrice(ticketApprox);

  for (let delta = -1; delta <= 1; delta += 0.01) {
    const candidate = calculateFromTicketPrice(ticketApprox + delta);
    const diffBest = Math.abs(best.totalPrice - target);
    const diffCand = Math.abs(candidate.totalPrice - target);

    if (
      diffCand < diffBest ||
      (diffCand === diffBest && candidate.ticketPrice > best.ticketPrice)
    ) {
      best = candidate;
    }
  }

  return {
    ...best,
    displayPrice: target,
  };
};

/** Utilise la décomposition renvoyée par l'API (source de vérité). */
export const breakdownFromEvent = (event) => {
  if (!event || Number(event.price) <= 0) {
    return emptyBreakdown();
  }

  if (event.totalPrice != null && event.stripeFee != null) {
    const ticketPrice = Number(event.price);
    const serviceFee = Number(event.stripeFee);
    const tps = Number(event.tps ?? 0);
    const tvq = Number(event.tvq ?? 0);

    return {
      ticketPrice,
      serviceFee,
      stripeFee: serviceFee,
      subtotal: Number(event.subtotal ?? ticketPrice + serviceFee),
      tps,
      tvq,
      taxes: round2(tps + tvq),
      totalPrice: Number(event.totalPrice),
      displayPrice: Number(event.totalPrice),
    };
  }

  return calculateFromTicketPrice(Number(event.price));
};

/** @deprecated Utiliser calculateFromTicketPrice ou calculateFromDisplayPrice */
export const calculateFinalPrice = calculateFromTicketPrice;

export const formatTicketPrice = (amount) =>
  new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
  }).format(amount ?? 0);
